import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash } from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Field, inputCls, PrimaryButton, GhostButton } from "@/components/admin/parts";
import { adminApi, getErrorMessage } from "@/lib/api";

const sel = `${inputCls} !py-2`;
const toText = (v) => (Array.isArray(v) ? v.join(", ") : v ?? "");

function ValueInput({ type, op, value, onChange, hubs, schema, testId }) {
  if (schema.no_value_ops.includes(op) || ["clear", "upsize_hub", "remove_matched"].includes(op)) return <div className="text-xs text-neutral-600 px-2 py-2.5">—</div>;
  if (type === "hub" || op === "set_hub") {
    return (
      <select value={Array.isArray(value) ? value[0] || "" : value || ""} onChange={(e) => onChange(e.target.value)} className={sel} data-testid={testId}>
        <option value="">Pick a hub…</option>
        {hubs.map((h) => <option key={h.id} value={h.id}>{h.name}</option>)}
      </select>
    );
  }
  const list = schema.list_ops.includes(op);
  return <input value={toText(value)} onChange={(e) => onChange(e.target.value)} placeholder={list ? "Comma-separated, e.g. 4T, 4 TONNE" : "Value"} className={`${inputCls} !py-2 font-mono text-sm`} data-testid={testId} />;
}

function CondRow({ c, schema, hubs, onChange, onRemove }) {
  const fact = schema.facts.find((f) => f.id === c.field) || schema.facts[0];
  const ops = schema.ops_by_type[fact.type];
  const set = (k, v) => onChange({ ...c, [k]: v, ...(k === "field" ? { op: schema.ops_by_type[schema.facts.find((f) => f.id === v).type][0], value: "" } : {}) });
  return (
    <div className="grid grid-cols-[1fr_1fr_1.4fr_32px] gap-2" data-testid="rule-cond-row">
      <select value={c.field} onChange={(e) => set("field", e.target.value)} className={sel} data-testid="rule-cond-field">{schema.facts.map((f) => <option key={f.id} value={f.id}>{f.label}</option>)}</select>
      <select value={c.op} onChange={(e) => set("op", e.target.value)} className={sel} data-testid="rule-cond-op">{ops.map((o) => <option key={o} value={o}>{schema.ops[o]}</option>)}</select>
      <ValueInput type={fact.type} op={c.op} value={c.value} onChange={(v) => set("value", v)} hubs={hubs} schema={schema} testId="rule-cond-value" />
      <button onClick={onRemove} className="p-2 rounded-lg text-neutral-500 hover:text-red-400"><Trash size={15} /></button>
    </div>
  );
}

function ActRow({ a, schema, hubs, onChange, onRemove }) {
  const act = schema.actions.find((x) => x.id === a.field) || schema.actions[0];
  const set = (k, v) => onChange({ ...a, [k]: v, ...(k === "field" ? { op: schema.actions.find((x) => x.id === v).ops[0], value: "" } : {}) });
  return (
    <div className="grid grid-cols-[1fr_1fr_1.4fr_32px] gap-2" data-testid="rule-act-row">
      <select value={a.field} onChange={(e) => set("field", e.target.value)} className={sel} data-testid="rule-act-field">{schema.actions.map((x) => <option key={x.id} value={x.id}>{x.label}</option>)}</select>
      <select value={a.op} onChange={(e) => set("op", e.target.value)} className={sel} data-testid="rule-act-op">{act.ops.map((o) => <option key={o} value={o}>{schema.action_ops[o]}</option>)}</select>
      <ValueInput type="text" op={a.op} value={a.value} onChange={(v) => set("value", v)} hubs={hubs} schema={schema} testId="rule-act-value" />
      <button onClick={onRemove} className="p-2 rounded-lg text-neutral-500 hover:text-red-400"><Trash size={15} /></button>
    </div>
  );
}

const listify = (rows, schema) => rows.map((c) => (schema.list_ops.includes(c.op) && !Array.isArray(c.value)
  ? { ...c, value: String(c.value || "").split(",").map((x) => x.trim()).filter(Boolean) }
  : c.field === "hub" && schema.list_ops.includes(c.op) ? { ...c, value: [].concat(c.value) } : c));

export function RuleEditor({ rule, schema, hubs, onClose, onSaved }) {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { setForm(rule ? { title: "", description: "", enabled: true, conditions: [], actions: [], stage: "specs", ...rule } : null); }, [rule]);
  if (!form) return null;
  const set = (k, v) => setForm({ ...form, [k]: v });
  const upd = (k, i, v) => set(k, form[k].map((x, j) => (j === i ? v : x)));

  const save = async () => {
    setSaving(true);
    const body = { ...form, conditions: listify(form.conditions, schema) };
    try {
      if (form.id) await adminApi.put(`/admin/rulebook/${form.id}`, body);
      else await adminApi.post("/admin/rulebook", body);
      toast.success("Rule saved");
      onSaved();
    } catch (e) { toast.error(getErrorMessage(e)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#0F0F0F] border-neutral-800 text-neutral-200 max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="rule-editor">
        <DialogHeader><DialogTitle className="font-heading text-white">{form.id ? "Edit rule" : "New rule"}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-1 sm:grid-cols-[1fr_220px] gap-3">
          <Field label="Title"><input value={form.title} onChange={(e) => set("title", e.target.value)} className={inputCls} data-testid="rule-title-input" placeholder="e.g. Adventure chassis ending 24 → Gen 1" /></Field>
          <Field label="Stage">
            <select value={form.stage} onChange={(e) => set("stage", e.target.value)} className={inputCls} data-testid="rule-stage-select">
              {schema.stages.map((s, i) => <option key={s.id} value={s.id}>{i + 1}. {s.label}</option>)}
            </select>
          </Field>
        </div>
        <div className="space-y-2 mt-2">
          <div className="text-[0.65rem] font-bold uppercase tracking-wider text-neutral-500">If (all must be true — leave empty for every van)</div>
          {form.conditions.map((c, i) => <CondRow key={i} c={c} schema={schema} hubs={hubs} onChange={(v) => upd("conditions", i, v)} onRemove={() => set("conditions", form.conditions.filter((_, j) => j !== i))} />)}
          <button onClick={() => set("conditions", [...form.conditions, { field: "series", op: "equals", value: "" }])} data-testid="rule-add-cond" className="inline-flex items-center gap-1 text-sm text-[#73bf44]"><Plus size={14} /> Add condition</button>
        </div>
        <div className="space-y-2 mt-3">
          <div className="text-[0.65rem] font-bold uppercase tracking-wider text-amber-300/80">Then</div>
          {form.actions.map((a, i) => <ActRow key={i} a={a} schema={schema} hubs={hubs} onChange={(v) => upd("actions", i, v)} onRemove={() => set("actions", form.actions.filter((_, j) => j !== i))} />)}
          <button onClick={() => set("actions", [...form.actions, { field: "battery_configuration", op: "set", value: "" }])} data-testid="rule-add-act" className="inline-flex items-center gap-1 text-sm text-[#73bf44]"><Plus size={14} /> Add action</button>
        </div>
        <Field label="Notes (optional)"><textarea value={form.description} onChange={(e) => set("description", e.target.value)} rows={2} className={inputCls} data-testid="rule-description" /></Field>
        <div className="flex justify-end gap-2 pt-1">
          <GhostButton onClick={onClose}>Cancel</GhostButton>
          <PrimaryButton onClick={save} disabled={saving} data-testid="rule-save">{saving ? "Saving…" : "Save rule"}</PrimaryButton>
        </div>
      </DialogContent>
    </Dialog>
  );
}
